import { LineBuffer, laplacian, clamp, requestMedia } from "../shared";

class Ferrofluid {
  constructor(ctx) {
    this.ctx = ctx; this.writesColors = true; this.magnets = []; this.peak = 0; this.spikes = 0;
    this.gfx = new LineBuffer(ctx.overlay, 1200, 0.75);
    this.addMagnet(0, 0); this.alloc();
  }
  alloc() {
    const n = this.ctx.count;
    this.d = new Float32Array(n); this.flux = new Float32Array(n); this.B2 = new Float32Array(n); this.A = new Float32Array(n);
    this.refill();
  }
  refill() {
    const { N, px } = this.ctx, R = 1.4 + 1.6 * this.ctx.params.volume, m = this.magnets[0] || { x: 0, z: 0 };
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
      const q = ((px[c] - m.x) ** 2 + (px[r] - m.z) ** 2) / (R * R);
      this.d[r * N + c] = q < 1 ? 0.55 * (1 - q) : 0;
    }
  }
  addMagnet(x, z) { if (this.magnets.length < 4) this.magnets.push({ x, z, bx: x, bz: z }); }
  nearest(x, z, lim) {
    let best = null, bd = lim;
    for (const m of this.magnets) { const d = Math.hypot(m.x - x, m.z - z); if (d < bd) { bd = d; best = m; } }
    return best;
  }
  pointer(e) {
    if (e.type === "down") {
      const near = this.nearest(e.x, e.z, 1.1);
      if (e.shift && near) { this.magnets = this.magnets.filter((m) => m !== near); return; }
      if (!near) this.addMagnet(e.x, e.z);
      this.drag = near || this.magnets[this.magnets.length - 1];
    } else if (e.type === "move" && this.drag) { this.drag.x = this.drag.bx = e.x; this.drag.z = this.drag.bz = e.z; }
    else if (e.type === "up") this.drag = null;
  }
  field(t) {
    const P = this.ctx.params, { N, px } = this.ctx, B2 = this.B2;
    let S = P.strength * this.ctx.g.force;
    if (P.music && this.an) S *= 0.3 + this.bass * P.react * 1.8;
    if (P.field === "pulse") S *= 0.5 + 0.5 * Math.sin(t * Math.PI * 2 * P.freq);
    for (const m of this.magnets) {
      if (P.field === "orbit" && m !== this.drag) { const a = t * P.freq * 2; m.x = m.bx + Math.cos(a) * 1.6; m.z = m.bz + Math.sin(a) * 1.6; }
      else if (m !== this.drag) { m.x = m.bx; m.z = m.bz; }
    }
    let peak = 0;
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
      let B = 0;
      for (const m of this.magnets) { const q = (px[c] - m.x) ** 2 + (px[r] - m.z) ** 2 + 2.56; B += S * 4.096 / (q * Math.sqrt(q)); }
      const i = r * N + c; B2[i] = B * B; if (B > peak) peak = B;
    }
    this.peak = peak;
  }
  flow() {
    const P = this.ctx.params, { N } = this.ctx, d = this.d, F = this.flux, B2 = this.B2;
    const mob = 0.09 * (1.02 - P.viscosity), chi = 0.9, sig = P.tension;
    const phi = this.A; // reuse as scratch potential
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
      const i = r * N + c; phi[i] = d[i] - chi * B2[i] - sig * laplacian(d, N, i, r, c);
    }
    F.fill(0);
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
      const i = r * N + c;
      if (c < N - 1) this.exchange(i, i + 1, d, phi, F, mob);
      if (r < N - 1) this.exchange(i, i + N, d, phi, F, mob);
    }
    for (let i = 0; i < d.length; i++) d[i] = Math.max(0, d[i] + F[i]);
  }
  exchange(i, j, d, phi, F, mob) {
    let q = mob * 0.5 * (d[i] + d[j]) * (phi[i] - phi[j]);
    if (q > 0) q = Math.min(q, d[i] * 0.2); else q = Math.max(q, -d[j] * 0.2);
    F[i] -= q; F[j] += q;
  }
  step(dt, t) {
    const P = this.ctx.params, { N, px, target, colors } = this.ctx;
    this.bands(); this.field(t);
    const subs = clamp(Math.round(3 * this.ctx.g.simSpeed), 1, 8);
    for (let s = 0; s < subs; s++) this.flow();
    const au = P.music && this.an ? P.react : 0, k = (Math.PI * 2) / Math.max(P.spacing * (1 - this.mid * 0.35 * au), 3 * this.ctx.sp), m0 = this.magnets[0] || { x: 0, z: 0 };
    const rot = P.field === "orbit" ? t * P.freq * 0.5 : 0;
    const dirs = [0, 1, 2].map((n) => [Math.cos(rot + (n * Math.PI * 2) / 3) * k, Math.sin(rot + (n * Math.PI * 2) / 3) * k]);
    let spikes = 0;
    const cell = (v) => px[clamp(Math.floor((v + this.ctx.half) / this.ctx.sp), 0, N - 1)], ox = cell(m0.x), oz = cell(m0.z);
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
      const i = r * N + c, x = px[c] - ox, z = px[r] - oz, di = this.d[i];
      const over = Math.max(0, 0.9 * this.B2[i] - P.threshold);
      const amp = Math.sqrt(over) * Math.min(1, di * 4) * 0.9;
      const lat = (Math.cos(dirs[0][0] * x + dirs[0][1] * z) + Math.cos(dirs[1][0] * x + dirs[1][1] * z) + Math.cos(dirs[2][0] * x + dirs[2][1] * z) + 1.5) / 4.5;
      const spike = amp * Math.pow(Math.max(0, lat), P.sharpness + this.treb * 4 * au) + (au ? this.treb * au * 0.12 * Math.sin(x * 9 + t * 14) * Math.cos(z * 9 - t * 11) * Math.min(1, di * 4) : 0);
      if (spike > 0.25) spikes++;
      target[i] = clamp(-1 + Math.min(di, 1.4) * 0.5 + spike * 1.7, -1, 1);
      const wet = Math.min(1, di * 10), tip = Math.min(1, spike * 1.4);
      colors[i * 3] = 0.34 - wet * (0.3 - tip * 0.2);
      colors[i * 3 + 1] = 0.38 - wet * (0.34 - tip * 0.06);
      colors[i * 3 + 2] = 0.46 - wet * (0.4 - tip * 0.35);
    }
    this.spikes = spikes; this.drawMagnets();
  }
  drawMagnets() {
    const L = this.gfx, y = this.ctx.top + 0.08; L.begin();
    for (const m of this.magnets) for (let k = 0; k < 48; k++) {
      const a0 = (k / 48) * Math.PI * 2, a1 = ((k + 1) / 48) * Math.PI * 2, R = 1.0;
      if (k % 2 === 0) L.seg(m.x + Math.cos(a0) * R, y, m.z + Math.sin(a0) * R, m.x + Math.cos(a1) * R, y, m.z + Math.sin(a1) * R, 1, 0.24, 0.6);
      if (k % 12 === 0) L.seg(m.x + Math.cos(a0) * 1.15, y, m.z + Math.sin(a0) * 1.15, m.x + Math.cos(a0) * 1.4, y, m.z + Math.sin(a0) * 1.4, 1, 0.5, 0.8);
    }
    L.end();
  }
  bands() {
    if (!this.an) { this.bass = this.mid = this.treb = 0; return; }
    this.an.getByteFrequencyData(this.bins);
    const hz = this.ac.sampleRate / this.an.fftSize, avg = (a, b) => { let s = 0, n = 0; for (let i = Math.max(1, (a / hz) | 0); i < b / hz; i++) { s += this.bins[i]; n++; } return n ? s / n / 255 : 0; };
    const sm = (o, v) => o + (v - o) * 0.35;
    this.bass = sm(this.bass, avg(20, 250)); this.mid = sm(this.mid, avg(250, 2000)); this.treb = sm(this.treb, Math.min(1, avg(2000, 8000) * 1.6));
  }
  async startAudio(kind, file) {
    this.stopAudio();
    const AC = window.AudioContext || window.webkitAudioContext, ac = (this.ac = new AC());
    this.an = ac.createAnalyser(); this.an.fftSize = 2048; this.an.smoothingTimeConstant = 0.7; this.bins = new Uint8Array(this.an.frequencyBinCount);
    try {
      if (kind === "mic") { this.src = "Requesting mic…"; await ac.resume?.(); this.stream = await requestMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: true } }); await ac.resume?.(); ac.createMediaStreamSource(this.stream).connect(this.an); this.src = "Microphone"; return "Microphone live — play music near it"; }
      const el = new Audio(URL.createObjectURL(file)); el.loop = true;
      const node = ac.createMediaElementSource(el); node.connect(this.an); node.connect(ac.destination);
      await ac.resume?.(); await el.play(); this.el = el; this.src = file.name.slice(0, 20); return `Playing ${file.name}`;
    } catch (e) { this.stopAudio(); throw e; }
  }
  stopAudio() { this.stream?.getTracks().forEach((t) => t.stop()); this.el?.pause(); this.ac?.close?.(); this.ac = this.an = this.stream = this.el = null; this.src = "No audio"; }
  action(id, payload) {
    if (id === "mic") return this.startAudio("mic");
    if (id === "audioFile") return this.startAudio("file", payload);
    if (id === "stopAudio") { this.stopAudio(); return "Audio stopped"; }
    if (id === "refill") { this.ctx.pushHistory(); this.refill(); return "Fluid reset into a single pool"; }
    if (id === "addMag") {
      if (this.magnets.length >= 4) return "Maximum of 4 magnets reached";
      this.ctx.pushHistory(); this.addMagnet((Math.random() - 0.5) * 6, (Math.random() - 0.5) * 6); return `${this.magnets.length} magnet(s) active`;
    }
    if (id === "removeMag") {
      if (!this.magnets.length) return "No magnets to remove";
      this.ctx.pushHistory(); this.magnets.pop(); return `${this.magnets.length} magnet(s) active`;
    }
  }
  onParams(key) { if (key === "volume") this.refill(); }
  resize() { this.alloc(); }
  snapshot() { return { m: this.magnets.map((m) => ({ ...m })), d: this.d.slice() }; }
  restore(s) { this.magnets = s.m.map((m) => ({ ...m })); if (s.d.length === this.d.length) this.d.set(s.d); }
  clear() { this.magnets = []; }
  telemetry() {
    let v = 0; for (let i = 0; i < this.d.length; i++) v += this.d[i];
    const bar = (v) => "▮".repeat(Math.round((v || 0) * 8)).padEnd(8, "·");
    const pc = (x) => String(Math.round((x || 0) * 99)).padStart(2, "0");
    return [["Audio", this.src || "No audio"], ["Bass · Mid · Treble", `${pc(this.bass)} · ${pc(this.mid)} · ${pc(this.treb)}`], ["Spikes", this.spikes > 0 ? `${this.spikes} cells` : "STABLE"], ["Magnets", this.magnets.length], ["Peak |H|", this.peak.toFixed(2)], ["Fluid volume", (v * this.ctx.sp * this.ctx.sp).toFixed(2)]];
  }
  dispose() { this.stopAudio(); this.gfx.dispose(); }
}

const ferrofluid = {
  id: "ferrofluid-lab", name: "Ferrofluid Music Lab", icon: "Droplets", rest: -1,
  desc: "Music drives the magnet: bass sets field strength and spike height, mids tighten the spike lattice, treble sharpens tips and shivers the surface. Start the mic or load an audio file. A pool of magnetic fluid flows toward the field and erupts into a hexagonal forest of spikes once the field passes the critical value (the Rosensweig instability). Tap to place a magnet under the plate, drag to move it, Shift-tap to remove it.",
  math: "Φ = ρg·h − ½μ₀χH² − σ∇²h   ·   spikes when H > H꜀",
  params: [
    { key: "music", label: "Music drives the field", type: "toggle", def: true, section: "Audio" },
    { key: "react", label: "Audio reactivity", type: "range", min: 0, max: 2.5, step: 0.05, def: 1.2, section: "Audio" },
    { key: "strength", label: "Magnet strength", type: "range", min: 0, max: 3, step: 0.05, def: 1.4, testid: "slider-ferro-strength" },
    { key: "field", label: "Field program", type: "select", options: [["static", "Static"], ["pulse", "Pulsing"], ["orbit", "Orbiting"]], def: "static" },
    { key: "freq", label: "Pulse / orbit rate", type: "range", min: 0.05, max: 2, step: 0.05, def: 0.4 },
    { key: "threshold", label: "Critical field H꜀", type: "range", min: 0, max: 2, step: 0.05, def: 0.35 },
    { key: "spacing", label: "Spike spacing λ꜀", type: "range", min: 0.3, max: 1.4, step: 0.05, def: 0.6 },
    { key: "sharpness", label: "Spike sharpness", type: "range", min: 1, max: 10, step: 0.5, def: 3 },
    { key: "volume", label: "Fluid volume", type: "range", min: 0.2, max: 1.6, step: 0.05, def: 0.9 },
    { key: "viscosity", label: "Viscosity", type: "range", min: 0, max: 0.95, step: 0.05, def: 0.45 },
    { key: "tension", label: "Surface tension", type: "range", min: 0, max: 0.2, step: 0.01, def: 0.06 },
  ],
  actions: [{ id: "mic", label: "Microphone", section: "Audio", testid: "ferro-mic" }, { id: "audioFile", label: "Load audio file", kind: "file", accept: "audio/*", section: "Audio", testid: "ferro-audio-input" }, { id: "stopAudio", label: "Stop audio", section: "Audio" }, { id: "addMag", label: "Add magnet" }, { id: "removeMag", label: "Remove magnet" }, { id: "refill", label: "Reset fluid" }],
  create: (ctx) => new Ferrofluid(ctx),
};
export default ferrofluid;
